import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import AddPaymentMethodModal, { initialPaymentState } from '../payment-method-modal/AddPaymentMethodModal';
import SignOutModal from '../sign-out-modal/SignOutModal';
import NotificationBanner from '../notificationBanner/NotificationBanner';
import { CartSVG } from '../svgs';
import { centsToDollars } from '../../services/account.service';
import {
  doesAccountHasPendingPaymentTransaction,
  doesUserHaveSettingAccess,
  getPaymentDetails,
} from '../../services/payment.service';
import { PermissionService } from '../../services/permission.service';
import { showToast } from '../../services/toast.service';
import { AccountRoles, AppRoles } from '../../types/enums';
import { PaymentMethod } from '../../types/interfaces/paymentMethod.interface';
import { ACCOUNT_PATHS, APP_PATHS } from '../../constants/routePaths';
import { BUTTON_CONSTANTS, NOTIFICATION_CONSTANTS, PAGE_HEADER_CONSTANTS, RSG_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';

const TitleBar = ({ showSignOut = false }: { showSignOut: boolean }): JSX.Element => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const activeAccount = useAppSelector(state => state.activeAccount);
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const cartItems = useAppSelector(state => state.transactionCart.transactionCartItems);
  const marketplaceCartQuantity = useAppSelector(state => state.marketplaceCart.marketplaceCartQuantity);
  const enabled = useAppSelector(state => state.user.enabled);
  const paymentStatus = useAppSelector(state => state.payment.addedPaymentMethod);
  const user = useAppSelector(state => state.user);
  const verified = useAppSelector(state => state.user.verified);

  const [addPaymentMethodModalVisible, setAddPaymentMethodModalVisible] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<PaymentMethod>(initialPaymentState);
  const [pendingACHPayment, setPendingACHPayment] = useState<boolean>();
  const [showPayNowButton, setShowPayNowButton] = useState<boolean>();
  const [userHaveSettingAccess, setUserHaveSettingAccess] = useState<boolean>();
  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);

  const checkPaymentValidity = () => {
    return activeAccount?.billingAmount?.outstandingBalanceCents > 0 || activeAccount?.billingAmount?.penaltyCents > 0;
  };

  useEffect(() => {
    const fetchSettingsAccess = async () => {
      if (accountId) {
        try {
          const hasSettingAccess = await doesUserHaveSettingAccess(accountId, user?.userId);
          setUserHaveSettingAccess(hasSettingAccess);
          checkPaymentValidity() && setShowPayNowButton(hasSettingAccess);
        } catch (error) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

    const fetchPendingPayment = async () => {
      if (accountId && permissionService.userHasSettingsPermission()) {
        try {
          const hasPendingPayment = await doesAccountHasPendingPaymentTransaction();
          setPendingACHPayment(hasPendingPayment);
        } catch (error) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

    fetchSettingsAccess();
    fetchPendingPayment();
  }, [activeAccount]);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (permissionService.userHasSettingsPermission() && accountId && paymentStatus) {
        const paymentData = await getPaymentDetails(accountId);
        paymentData && setPaymentInfo(paymentData);
      }
    };
    fetchPaymentDetails();
  }, [addPaymentMethodModalVisible, accountId]);

  const accountSettingsLink = (
    <Link to={ACCOUNT_PATHS.ACCOUNT_SETTINGS} className="payment-details-link" state={{ activeTab: 2 }}>
      {PAGE_HEADER_CONSTANTS.SETTINGS}
    </Link>
  );

  const getNotificationMessage = () => {
    if (userHaveSettingAccess != undefined && userHaveSettingAccess) {
      return <p>To access all the features of this account, please go to {accountSettingsLink} and add your payment details.</p>;
    } else {
      return <p>{NOTIFICATION_CONSTANTS.ADD_PAYMENT_DETAILS}</p>;
    }
  };

  return (
    <div className="title-bar">
      <div className="logo">{RSG_CONSTANTS.RSG_TITLE}</div>
      {showSignOut ? (
        <div className="sign-out-link">
          <SignOutModal />
        </div>
      ) : (
        <div className="notification-cart-container">
          {user?.accounts?.length > 0 && paymentStatus != undefined && !paymentStatus && (
            <>
              <NotificationBanner
                children={<div className="payment-details-not-found-notification">{getNotificationMessage()}</div>}
              />
            </>
          )}
          {user?.accounts?.length > 0 &&
            paymentStatus != undefined &&
            paymentStatus &&
            (activeAccount?.billingAmount?.outstandingBalanceCents > 0 ? (
              <>
                {pendingACHPayment != undefined &&
                  (!pendingACHPayment ? (
                    <NotificationBanner
                      children={
                        <div className="pay-now-notification">
                          <p className={`${!showPayNowButton ? 'full-width-pay-now-notification' : ''}`}>
                            {activeAccount?.billingAmount?.penaltyCents > 0 ? (
                              /* Here we need to add the quickbooks late fee in total fine amount in case we turn on the penalty */
                              <>
                                This account has a balance due of{' '}
                                <b>${centsToDollars(activeAccount?.billingAmount?.totalFineAmountCents)}</b>. (Includes{' '}
                                <b>${centsToDollars(activeAccount?.billingAmount?.outstandingBalanceCents)}</b> outstanding
                                balance, <b>${centsToDollars(activeAccount?.billingAmount?.lateFeeCents)}</b> late fee,{' '}
                                <b> ${centsToDollars(activeAccount?.billingAmount?.financeChargeCents)}</b> finance charge of{' '}
                                {activeAccount?.billingAmount?.financeChargeRate}%)
                              </>
                            ) : (
                              <>
                                This account has a balance due of{' '}
                                <b>
                                  $
                                  {centsToDollars(
                                    activeAccount?.billingAmount?.totalFineAmountCents +
                                      (activeAccount?.billingAmount?.quickbooksLateFeeCents ?? 0),
                                  )}
                                </b>
                                . Please pay the balance to avoid additional charges.
                              </>
                            )}
                          </p>
                          {showPayNowButton != undefined && showPayNowButton && (
                            <button
                              className="button green small pay-now-button"
                              type="button"
                              onClick={() => setAddPaymentMethodModalVisible(true)}>
                              {BUTTON_CONSTANTS.PAY_NOW}
                            </button>
                          )}
                        </div>
                      }
                    />
                  ) : (
                    <>
                      <NotificationBanner
                        children={
                          <div className="payment-details-not-found-notification">
                            <p>{NOTIFICATION_CONSTANTS.PENDING_ACH_PAYMENT}</p>
                          </div>
                        }
                      />
                    </>
                  ))}
              </>
            ) : (
              activeAccount?.billingAmount?.outstandingBalanceCents <= 0 &&
              activeAccount?.locked && (
                <>
                  <NotificationBanner
                    children={
                      <div className="payment-details-not-found-notification">
                        <p>{NOTIFICATION_CONSTANTS.ACCOUNT_LOCKED}</p>
                      </div>
                    }
                  />
                </>
              )
            ))}

          {(permissionService.userHasRSGAdminPermission() || permissionService.userHasInventoryPermission()) && (
            <Link className="button icon" to={APP_PATHS.TRANSACTION_CART}>
              <CartSVG />
              {(cartItems.length > 0 || marketplaceCartQuantity > 0) && <span className="cart-item" />}
            </Link>
          )}

          {accountId && addPaymentMethodModalVisible && (
            <AddPaymentMethodModal
              title={BUTTON_CONSTANTS.PAY_NOW}
              accountId={accountId}
              paymentDetails={paymentInfo}
              isOpen={addPaymentMethodModalVisible}
              setIsOpen={setAddPaymentMethodModalVisible}
              onclick={() => setAddPaymentMethodModalVisible(false)}
              buttonAction={'PayNow'}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TitleBar;
